import React, { useState, useCallback } from 'react';
import { MdCheck, MdContentCopy } from 'react-icons/md';
import { useTranslation } from '../localization/useAppTranslation';
import { logger } from '../utils/logger';
import '../styles/CopyMarkdownButton.css';

const moduleLogger = logger.getModule('CopyMarkdownButton');

interface CopyMarkdownButtonProps {
  content: string;
  className?: string;
  buttonSize?: 'small' | 'medium';
  showLabel?: boolean;
}

/**
 * Reusable button component for copying markdown content to clipboard.
 * Manages its own state and provides visual feedback.
 */
export const CopyMarkdownButton: React.FC<CopyMarkdownButtonProps> = ({
  content,
  className = '',
  buttonSize = 'small',
  showLabel = true,
}) => {
  const { t } = useTranslation('chat');
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content);
      setIsCopied(true);
      
      // Clear any existing timeout before setting a new one
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Reset the copied state after 2 seconds
      timeoutRef.current = setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      moduleLogger.error('Failed to copy to clipboard', { error });
    }
  }, [content]);

  // Clean up timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const buttonClass = [
    'copy-markdown-button',
    isCopied && 'copied',
    !showLabel && 'copy-markdown-button--icon-only',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const actionLabel = t('copyMarkdown.title');
  const statusLabel = isCopied ? t('copyMarkdown.copied') : actionLabel;

  return (
    <button
      type="button"
      className={buttonClass}
      onClick={handleCopy}
      title={actionLabel}
      aria-label={statusLabel}
    >
      {isCopied ? <MdCheck aria-hidden="true" /> : <MdContentCopy aria-hidden="true" />}
      {showLabel && (isCopied ? t('copyMarkdown.copied') : t('copyMarkdown.action'))}
    </button>
  );
};
